/**
 * Configuration interface for valueset management
 * This allows different valueset types to have their own configuration
 */
export interface ValueSetConfig {
  // Display name for this valueset type (e.g., "Tags", "Categories", "Statuses")
  displayName: string;
  
  // Plural display name
  displayNamePlural: string;
  
  // Available code systems for this valueset type
  codeSystems: {
    code: string;
    display: string;
    systemUrl: string;
    color?: string; // Optional color for UI display
  }[];
  
  // Available resource types that can be assigned to valuesets
  resourceTypes: {
    code: string;
    display: string;
  }[];
  
  // Optional: Custom validation rules
  validation?: {
    requireResources?: boolean;
    requireCodeSystem?: boolean;
    minResources?: number;
  };
  
  // Optional: Custom field configurations
  fields?: {
    showVersion?: boolean;
    showDescription?: boolean;
    showComments?: boolean;
  };
}

/**
 * Tags valueset configuration
 * This is a specific instance of valueset configuration for tags
 * 
 * NOTE: Code Systems are NOT hardcoded - they are managed dynamically per organization
 * The backend automatically creates ONE CodeSystem per organization
 */
export const tagsValueSetConfig: ValueSetConfig = {
  displayName: 'Tag',
  displayNamePlural: 'Tags',
  codeSystems: [
    // Code Systems are managed dynamically per organization
    // The backend creates ONE CodeSystem per organization automatically
    // This array is kept empty - no hardcoded values
  ],
  resourceTypes: [
    // Currently only Patient is supported as per Feature #24081 requirements
    { code: 'Patient', display: 'Patient' },
    // Future resource types can be added here:
    // { code: 'Encounter', display: 'Encounter' },
    // { code: 'Observation', display: 'Observation' },
  ],
  validation: {
    requireResources: true,
    requireCodeSystem: false, // CodeSystem is auto-created by backend
    minResources: 1,
  },
  fields: {
    showVersion: true,
    showDescription: true,
    showComments: true,
  },
};

/**
 * Helper function to get system URL from code system code
 */
export function getSystemUrlFromConfig(config: ValueSetConfig, codeSystemCode: string): string {
  const codeSystem = config.codeSystems.find((cs) => cs.code === codeSystemCode);
  return codeSystem?.systemUrl || `https://health.us/fhir/CodeSystem/${codeSystemCode.toLowerCase()}`;
}

/**
 * Helper function to get color for code system
 */
export function getCodeSystemColor(config: ValueSetConfig, codeSystemCode: string): string {
  const codeSystem = config.codeSystems.find((cs) => cs.code === codeSystemCode);
  return codeSystem?.color || '#637381';
}

